export const PLATFORM_ROLE_IDS = Object.freeze([
  "platform_super_admin",
  "platform_finance",
  "platform_ops",
  "platform_support",
  "platform_content_manager",
  "platform_auditor",
  "platform_integrations_admin",
]);

const PLATFORM_ROLE_SET = new Set(PLATFORM_ROLE_IDS);

function stringList(value, field) {
  if (!Array.isArray(value) || value.some((item) => typeof item !== "string" || !item.trim())) {
    throw new TypeError(`Invalid platform access field: ${field}`);
  }
  return [...new Set(value.map((item) => item.trim()))];
}

/**
 * Validate the platform-access response before it becomes authorization state.
 * A partial or differently shaped response is rejected instead of being treated as permission.
 */
export function normalizePlatformAccess(value) {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    throw new TypeError("Invalid platform access response");
  }

  const roles = stringList(value.roles, "roles");
  const capabilities = stringList(value.capabilities, "capabilities");
  const assignableRoles = value.assignable_roles === undefined
    ? []
    : stringList(value.assignable_roles, "assignable_roles");
  const labels = value.role_labels;
  const redactions = value.redactions;

  if (!labels || typeof labels !== "object" || Array.isArray(labels)) {
    throw new TypeError("Invalid platform access field: role_labels");
  }
  if (
    !redactions ||
    typeof redactions !== "object" ||
    Array.isArray(redactions) ||
    typeof redactions.commercial !== "boolean" ||
    typeof redactions.credentials !== "boolean"
  ) {
    throw new TypeError("Invalid platform access field: redactions");
  }
  if (roles.some((role) => !PLATFORM_ROLE_SET.has(role))) {
    throw new TypeError("Invalid platform role in access response");
  }
  if (assignableRoles.some((role) => !PLATFORM_ROLE_SET.has(role))) {
    throw new TypeError("Invalid assignable platform role in access response");
  }
  if (roles.some((role) => typeof labels[role] !== "string" || !labels[role].trim())) {
    throw new TypeError("Missing platform role label");
  }

  return Object.freeze({
    roles: Object.freeze(roles),
    roleLabels: Object.freeze(
      Object.fromEntries(
        PLATFORM_ROLE_IDS
          .filter((role) => typeof labels[role] === "string" && labels[role].trim())
          .map((role) => [role, labels[role].trim()])
      )
    ),
    capabilities: Object.freeze(capabilities),
    assignableRoles: Object.freeze(assignableRoles),
    redactions: Object.freeze({
      commercial: redactions.commercial,
      credentials: redactions.credentials,
    }),
  });
}

export function isTenantlessPlatformUser(user) {
  return !!(
    user &&
    user.tenant_id === null &&
    Array.isArray(user.roles) &&
    user.roles.some((role) => PLATFORM_ROLE_SET.has(role))
  );
}

export function hasPlatformCapability(access, capability) {
  if (!access || typeof capability !== "string" || !capability) return false;
  const capabilities = access.capabilities;
  return Array.isArray(capabilities) && (
    capabilities.includes("*") || capabilities.includes(capability)
  );
}
